import numbro from 'numbro';
import numbroLanguages from 'numbro/dist/languages.min';
import {isNumberValid} from 'common/validators/number-validator';

const registerLanguage = (languageTag) => {
  if (!numbroLanguages.hasOwnProperty(languageTag)) {
    return;
  }
  const language = numbroLanguages[languageTag];
  numbro.registerLanguage(language);
};

const setLanguage = (locale) => {
  registerLanguage(locale);
  numbro.setLanguage(locale);
};

// Grab locale code from window.settings.locale and
// set on Numbro. All Numbro operations, globally,
// will now occur in the passed locale.
if (window && window.settings && window.settings.locale) {
  const locale = window.settings.locale.replace('_', '-');
  setLanguage(locale);
} else {
  setLanguage('en-US');
}

// for well known currencies, let numbro format, it does a better job than Intl
const knownCurrencies = {
  NOK: 'nb-NO',
  CNY: 'zh-CN',
};

// Core number formatting. All number formatting in
// JS should happen via this function.
export const formatNumber = (
  // Pass any valid number for value. If you
  // pass an invalid number, you get back "-".
  value,

  // A Numbro output type. Choices are:
  // - 'number'
  // - 'currency'
  // - 'percent'
  // The default is 'number', so if that's what you want,
  // you don't need to pass anything.
  // Note that if you pass a custom formatting object
  // as the next arg, output is ignored.
  output = 'number',

  // A Numbro formatting object that will override defaults.
  // https://numbrojs.com/format.html
  // This is intended as an escape hatch for very rare cases.
  // You should strive not to use it. The default rules were
  // hard to get right, please let them do their thing. Or
  // consider improving the defaults if you feel something
  // is missing. Defaults are below in getFormatForNumber().
  // Note that if you pass a custom formatting object,
  // the previous arg, output, is ignored.
  customFormat = null,

  // sometimes we encode currency into objects (reports/storyboards)
  // passing in currencyCode will override viewers org currency
  currencyCode = null,

  decimalPlaces = null,
) => {
  // Return a dash if the incoming value is a dud.
  if (!isNumberValid(value)) {
    return '\u2013';
  }

  // For percentages, Numbro wants a value between 0 and 1.
  // Our values are between 0 and 100 (or beyond).
  // We need to format percentages as regular numbers,
  // and crudely append the "%" symbol.
  if (output === 'percent' && !customFormat) {
    const format = getFormatForNumber(value, 'number');
    return `${numbro(value).format(format)}%`;
  }

  // numbro assumes local currency (USD org currency will be overriden by locale symbol)
  // we separate locale from currency, so we need to force currency code usage
  if (output === 'currency') {
    const code = currencyCode || window.settings.organization.currency.code;
    if (code in knownCurrencies) {
      const orgLocale = numbro.language;
      const currencyLocale = knownCurrencies[code];
      setLanguage(currencyLocale);
      const format = getFormatForNumber(value, output);
      const formatted = numbro(value).format(format);

      if (typeof orgLocale === 'string') {
        setLanguage(orgLocale); // reset
      }
      return formatted;
    }
    return new Intl.NumberFormat(numbro.language, {
      style: 'currency',
      currency: code,
      maximumFractionDigits: decimalPlaces || 2,
      minimumFractionDigits: 0,
    }).format(value);
  }

  const format =
    customFormat || getFormatForNumber(value, output, decimalPlaces);
  return numbro(value).format(format);
};

// Returns a value formatted as a plain number, with units appended.
// A convenience function, no more.
export const formatNumberWithUnits = (value, units) => {
  return `${formatNumber(value)} ${units || ''}`.trim();
};

// Returns a Numbro formatting object for a given number.
// Formatting reference: https://numbrojs.com/format.html
export const getFormatForNumber = (value, output, decimalPlaces = null) => {
  if (decimalPlaces) {
    return {
      mantissa: decimalPlaces,
      trimMantissa: true,
      output,
    };
  }

  const abs = Math.abs(value);
  switch (true) {
    case abs === 0:
      return {
        mantissa: 0,
        output,
      };
    case abs < 0.0001:
      return {
        mantissa: 5,
        output,
        trimMantissa: true,
      };
    case abs < 0.01:
      return {
        mantissa: 4,
        output,
        trimMantissa: true,
      };
    case abs < 100:
      return {
        mantissa: 2,
        output,
        trimMantissa: true,
      };
    case abs < 1000000:
      return {
        mantissa: 0,
        output,
        thousandSeparated: true,
        trimMantissa: true,
      };
    default:
      return {
        average: true,
        mantissa: 2,
        output,
        thousandSeparated: true,
        trimMantissa: true,
      };
  }
};

// TODO Implement a way to set the Numbro locale based
// on the backend currency unit.
// export const setLocaleFromCurrencyUnit = (currency) => {
// // numbro.setLanguage('en-US');
// };
